import React, { ComponentType, useEffect, useState } from 'react';

import { BaseTextFieldProps } from '@material-ui/core';
import { isArray, head, set } from 'lodash';

import { LanguageCode } from 'components/LanguageCode/LanguageCode';
import { TabPanelItem } from 'components/TabsComponent/TabPanelItem/TabPanelItem';
import { LocalizationObject, ValidatorType } from 'types/common';

export interface WithLocalizationProps extends BaseTextFieldProps {
  fieldName: string;
  pageLanguageTabValue?: number;
  value?: LocalizationObject[] | string;
  multilanguage?: boolean;
  isLanguageCodeVisible?: boolean;
  handleValidationCheck?: (langCode: string, fieldName: string) => (isValid: boolean) => void;
  handleChange: (item: any) => void;
  handleGetValue: (item: Record<string, string>) => string;
  additionalClassName?: string;
  validators?: ValidatorType[];
}

export interface ComponentWithLocalizationProps {
  value: string;
  fieldName: string;
}

export const withLocalization =
  <T,>(Component: ComponentType<T & ComponentWithLocalizationProps>) =>
  ({
    multilanguage,
    value,
    fieldName,
    handleChange,
    pageLanguageTabValue,
    handleGetValue,
    handleValidationCheck,
    isLanguageCodeVisible = true,
    additionalClassName = '',
    ...props
  }: WithLocalizationProps): JSX.Element => {
    const [languages, setLanguages] = useState<string[]>([]);

    useEffect(() => {
      if (multilanguage && isArray(value)) {
        setLanguages(value?.map(({ languageCode }) => languageCode));
      }
    }, [value?.length]);

    const handleMultilanguageChange = (index: number) => (item: Record<string, string>) => {
      if (!isArray(value)) {
        return;
      }

      handleChange({
        [fieldName]: [...set(value, [index, 'value'], handleGetValue(item))],
      });
    };

    const handleOneLanguageChange = (item: Record<string, string>) =>
      handleChange({ [fieldName]: handleGetValue(item) });

    if (multilanguage && isArray(value)) {
      return value.length > 1 ? (
        <>
          {isLanguageCodeVisible && (
            <LanguageCode className={additionalClassName} code={languages[pageLanguageTabValue!]} />
          )}
          {value?.map((item, index) => (
            <TabPanelItem
              className={additionalClassName}
              value={pageLanguageTabValue}
              index={index}
              key={item.languageCode}
            >
              <Component
                {...(props as T)}
                value={value[index].value || ''}
                fieldName={fieldName}
                handleChange={handleMultilanguageChange(index)}
                onValidationChange={handleValidationCheck?.(item.languageCode, fieldName)}
              />
            </TabPanelItem>
          ))}
        </>
      ) : (
        <Component
          {...(props as T)}
          additionalClassName={additionalClassName}
          value={head(value)?.value || ''}
          fieldName={fieldName}
          handleChange={handleMultilanguageChange(0)}
        />
      );
    }

    return (
      <Component
        {...(props as T)}
        value={(value as string) || ''}
        fieldName={fieldName}
        handleChange={handleOneLanguageChange}
      />
    );
  };
